<template>
  <div class="card border-0 shadow-none rounded-3 mb-4">
    <div class="card-body p-4 p-xl-40">
      <form @submit.prevent="handleSubmit">
        <div class="intro-card mb-4">
          <div class="intro-icon">
            <i class="ri-shield-keyhole-line"></i>
          </div>
          <div>
            <h5 class="mb-1">{{ $t("group_permission_page.create_header") }}</h5>
            <p class="mb-0 text-muted">
              {{ $t("group_permission_page.create_description") }}
            </p>
          </div>
        </div>

        <div class="row g-3">
          <div class="col-md-6">
            <div class="field-group">
              <label class="field-label">{{ $t("group_permission_page.role_name") }}</label>
              <input
                type="text"
                class="form-control field-input"
                :placeholder="$t('group_permission_page.role_name_placeholder')"
                v-model="role.role_name"
                required
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="field-group">
              <label class="field-label">{{ $t("group_permission_page.description_label") }}</label>
              <input
                type="text"
                class="form-control field-input"
                :placeholder="$t('group_permission_page.description_placeholder')"
                v-model="role.description"
              />
            </div>
          </div>
        </div>

        <div class="permission-toolbar mt-4 mb-4">
          <div class="checkbox style-four">
            <input
              class="form-check-input"
              type="checkbox"
              id="select_all"
              :checked="isAllSelected"
              @change="toggleAll"
            />
            <label class="form-check-label fw-semibold" for="select_all">
              {{ $t("group_permission_page.select_all_permissions") }}
            </label>
          </div>
          <span class="permission-hint">{{ $t("group_permission_page.toggle_hint") }}</span>
        </div>

        <div class="row g-4">
          <div v-for="(group, gIndex) in localizedPermissionGroups" :key="gIndex" class="col-xl-4 col-lg-6">
            <div class="permission-card">
              <div class="permission-card__header">
                <h6 class="mb-0">{{ group.name }}</h6>
              </div>
              <div class="permission-card__body">
                <div v-for="(perm, pIndex) in group.permissions" :key="pIndex" class="permission-item">
                  <div class="checkbox style-four">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="`permission-${gIndex}-${perm.key}`"
                      v-model="role[perm.key]"
                      :true-value="1"
                      :false-value="0"
                    />
                    <label class="form-check-label" :for="`permission-${gIndex}-${perm.key}`">
                      {{ perm.label }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-12">
            <button class="btn style-one px-4 h-55" type="submit" :disabled="isSubmitting">
              {{ isSubmitting ? $t("group_permission_page.creating") : $t("group_permission_page.create_role") }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import rolePermissionService, {
  type PermissionStructureGroup,
  type RolePermission,
} from "@/services/role_permission";
import Swal from 'sweetalert2';
import { localizePermissionStructure } from "@/utils/rolePermissionI18n";

type EditableRole = Partial<RolePermission> &
  Record<string, string | number | boolean | undefined> & {
    role_name: string;
    description: string;
    status: number;
  };

export default defineComponent({
  name: "CreateGroupPermission",
  setup() {
    const router = useRouter();
    const { t, te } = useI18n();
    const isSubmitting = ref(false);
    const permissionGroups = ref<PermissionStructureGroup[]>([]);

    const role = ref<EditableRole>({
      role_name: "",
      description: "",
      status: 1,
    });

    const localizedPermissionGroups = computed(() =>
      localizePermissionStructure(permissionGroups.value, t, te),
    );

    onMounted(async () => {
      try {
        const structure = await rolePermissionService.getPermissionStructure();
        permissionGroups.value = structure;
        
        // Initialize permissions in role object
        structure.forEach((group) => {
          group.permissions.forEach((perm) => {
            if (role.value[perm.key] === undefined) {
              role.value[perm.key] = 0;
            }
          });
        });
      } catch (error) {
        console.error("Failed to fetch permission structure:", error);
        Swal.fire({
          icon: 'error',
          title: t("group_permission_page.error_title"),
          text: t("group_permission_page.load_structure_failed_text")
        });
      }
    });

    const isAllSelected = computed(() => {
      if (permissionGroups.value.length === 0) return false;
      for (const group of permissionGroups.value) {
        for (const perm of group.permissions) {
          if (!role.value[perm.key]) return false;
        }
      }
      return true;
    });

    const toggleAll = (e: Event) => {
      const checked = (e.target as HTMLInputElement).checked;
      const val = checked ? 1 : 0;
      permissionGroups.value.forEach(group => {
        group.permissions.forEach((perm) => {
          role.value[perm.key] = val;
        });
      });
    };

    const handleSubmit = async () => {
      isSubmitting.value = true;
      try {
        await rolePermissionService.createRole(role.value);
        Swal.fire({
          icon: 'success',
          title: t("group_permission_page.success_title"),
          text: t("group_permission_page.role_created_successfully"),
          timer: 1500,
          showConfirmButton: false
        });
        router.push('/group-permission');
      } catch (error) {
        console.error("Error creating role:", error);
        Swal.fire({
          icon: 'error',
          title: t("group_permission_page.error_title"),
          text: t("group_permission_page.failed_to_create_role")
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      role,
      permissionGroups,
      localizedPermissionGroups,
      isSubmitting,
      handleSubmit,
      isAllSelected,
      toggleAll
    };
  },
});
</script>

<style scoped>
.intro-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 20px;
  border: 1px solid #e6ebfb;
  border-radius: 18px;
  background: linear-gradient(180deg, #f8faff 0%, #ffffff 100%);
}

.intro-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #eef2ff;
  color: #5d5fef;
  font-size: 20px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.field-input {
  min-height: 54px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  box-shadow: none;
  padding: 14px 18px;
}

.field-input:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.permission-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border: 1px solid #edf0f7;
  border-radius: 18px;
  background: #fbfcff;
}

.permission-hint {
  font-size: 13px;
  color: #7d8aa2;
}

.permission-card {
  height: 100%;
  border: 1px solid #edf0f7;
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
}

.permission-card__header {
  padding: 16px 18px;
  border-bottom: 1px solid #edf0f7;
  background: #f7f9ff;
}

.permission-card__header h6 {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.permission-card__body {
  padding: 14px 18px 16px;
}

.permission-item + .permission-item {
  margin-top: 12px;
}

.permission-item :deep(.form-check-input) {
  border-color: #605dff;
}

.permission-item :deep(.form-check-label) {
  font-size: 14px;
  color: #334155;
}
</style>
